import { apiRequest, ApiError } from "@/lib/api/client";
import { getAuthToken } from "@/lib/api/token-store";

/**
 * REST client for the staff message-template library (MESSAGE_TEMPLATES.md).
 * Mirrors src/lib/api/queues.js. Every call is employee-gated server-side and
 * tenant-scoped to the caller; the editor never sends tenantId or ownerId — scope
 * travels as the `shared` boolean and the server forces personal rows to the caller.
 */

function createAuthHeaders(token, headers = {}) {
  const normalizedToken = String(token || getAuthToken() || "").trim();
  if (!normalizedToken) {
    throw new ApiError("authorization token is required", 401);
  }
  return {
    Authorization: `Bearer ${normalizedToken}`,
    ...headers,
  };
}

function jsonHeaders(token) {
  return {
    ...createAuthHeaders(token),
    "Content-Type": "application/json",
  };
}

function requireTemplateId(templateId) {
  const id = String(templateId || "").trim();
  if (!id) {
    throw new ApiError("templateId is required", 400);
  }
  return id;
}

/** Coerce one server row into the shape the editor and picker consume. */
function normalizeTemplate(raw) {
  return {
    id: String(raw?.id || "").trim(),
    shared: Boolean(raw?.shared),
    ownerId: String(raw?.ownerId || ""),
    title: String(raw?.title || ""),
    category: String(raw?.category || ""),
    body: String(raw?.body || ""),
    shortcut: String(raw?.shortcut || ""),
    order: Number(raw?.order) || 0,
    // canEdit is server-computed (shared rows need settings:manage) so the editor
    // never has to re-derive the permission rule.
    canEdit: Boolean(raw?.canEdit),
    createdAt: String(raw?.createdAt || ""),
    updatedAt: String(raw?.updatedAt || ""),
  };
}

/** Body accepted by create/update. Scope is a boolean; the server owns ownerId. */
function templateBody(draft) {
  if (!draft || typeof draft !== "object" || Array.isArray(draft)) {
    throw new ApiError("body is required", 400);
  }
  return JSON.stringify({
    shared: Boolean(draft.shared),
    title: String(draft.title ?? ""),
    category: String(draft.category ?? ""),
    body: String(draft.body ?? ""),
    shortcut: String(draft.shortcut ?? ""),
    order: Number(draft.order) || 0,
  });
}

/**
 * GET /api/admin/message-templates — the shared library plus the caller's own.
 * @returns {Promise<{items: Array<object>, canEditShared: boolean}>}
 */
export async function fetchMessageTemplates({ token } = {}) {
  const payload = await apiRequest(`/api/admin/message-templates`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
  return {
    items: Array.isArray(payload?.items) ? payload.items.map(normalizeTemplate) : [],
    canEditShared: Boolean(payload?.canEditShared),
  };
}

/**
 * GET /api/admin/message-templates/variables — the {{token}} catalog that drives the
 * editor's chip palette, plus example values for the live preview.
 * @returns {Promise<{variables: Array<object>, sample: Record<string,string>}>}
 */
export async function fetchTemplateVariables({ token } = {}) {
  const payload = await apiRequest(`/api/admin/message-templates/variables`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
  return {
    variables: Array.isArray(payload?.variables)
      ? payload.variables.map((variable) => ({
        key: String(variable?.key || ""),
        group: String(variable?.group || ""),
        label: String(variable?.label || ""),
        description: String(variable?.description || ""),
        example: String(variable?.example || ""),
        source: String(variable?.source || "server"),
      }))
      : [],
    sample: payload?.sample && typeof payload.sample === "object" ? payload.sample : {},
  };
}

/** POST /api/admin/message-templates — create. Shared rows need settings:manage. */
export async function createMessageTemplate({ token, draft } = {}) {
  const payload = await apiRequest(`/api/admin/message-templates`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: templateBody(draft),
  });
  return normalizeTemplate(payload);
}

/** PUT /api/admin/message-templates/{templateID} — replace the editable fields. */
export async function updateMessageTemplate({ token, templateId, draft } = {}) {
  const id = requireTemplateId(templateId);
  const payload = await apiRequest(`/api/admin/message-templates/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: jsonHeaders(token),
    body: templateBody(draft),
  });
  return normalizeTemplate(payload);
}

/** DELETE /api/admin/message-templates/{templateID} — soft-delete (204 → null). */
export async function deleteMessageTemplate({ token, templateId } = {}) {
  const id = requireTemplateId(templateId);
  return apiRequest(`/api/admin/message-templates/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: createAuthHeaders(token),
  });
}

/**
 * GET /api/admin/users/{userID}/template-context — the {{token}} → value map for one
 * patient: their record, next appointment, last visit, and the calling staff member.
 *
 * Client-sourced tokens (date helpers, the WhatsApp number label) are NOT in this
 * payload — buildClientTemplateVars supplies those and the caller merges them over.
 *
 * @returns {Promise<{userId: string, vars: Record<string,string>, resolvedAt: string}>}
 */
export async function fetchTemplateContext({ token, userId } = {}) {
  const id = String(userId || "").trim();
  if (!id) {
    throw new ApiError("userId is required", 400);
  }
  const payload = await apiRequest(`/api/admin/users/${encodeURIComponent(id)}/template-context`, {
    method: "GET",
    headers: createAuthHeaders(token),
    cache: "no-store",
  });
  const rawVars = payload?.vars && typeof payload.vars === "object" ? payload.vars : {};
  const vars = {};
  for (const [key, value] of Object.entries(rawVars)) {
    if (typeof value === "string") vars[key] = value;
  }
  return {
    userId: String(payload?.userId || id),
    vars,
    resolvedAt: String(payload?.resolvedAt || ""),
  };
}
